'use client'

import * as React from 'react'

import { NavItem } from '@/types/nav'
import { Locale, defaultLocale } from '@/i18n/routing'
import { Link, usePathname } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { ArrowRight, ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
interface MainNavProps {
  items?: NavItem[]
  locale: Locale
}

export function MainNav({ items, locale }: MainNavProps) {
  const t = useTranslations('MainNav')
  const linkLocale = locale === defaultLocale ? undefined : locale
  const pathname = usePathname()

  const isActive = React.useCallback(
    (href: string) => {
      if (!pathname) return false
      if (!href.startsWith('/')) return false
      if (href === '/') return pathname === '/'
      return pathname === href || pathname.startsWith(`${href}/`)
    },
    [pathname],
  )

  return (
    <div className="flex gap-6 md:gap-10">
      <Link
        href="/"
        locale={linkLocale}
        className="flex items-center space-x-3"
      >
        <Image src="/logo.png" alt="logo" width={48} height={48} priority />
        <span className="text-xl font-bold text-foreground transition-colors">
          {t('title')}
        </span>
      </Link>
      {items?.length ? (
        <nav className="flex gap-6">
          {items?.map((item) => {
            if (!item.href) return null
            const active = isActive(item.href)

            if (item.children?.length) {
              return (
                <DropdownMenu key={item.href} modal={false}>
                  <DropdownMenuTrigger
                    disabled={item.disabled}
                    className={cn(
                      'group flex items-center gap-1 rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4',
                      active
                        ? 'font-bold text-foreground'
                        : 'text-muted-foreground hover:text-primary',
                    )}
                  >
                    {item.title}
                    <ChevronDown
                      aria-hidden="true"
                      className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180"
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sideOffset={12}
                    className="w-72 max-w-[calc(100vw-2rem)] p-2"
                  >
                    {item.children.map((child) => (
                      <DropdownMenuItem
                        key={child.href}
                        asChild
                        className="cursor-pointer px-3 py-3"
                      >
                        <Link
                          href={child.href}
                          locale={linkLocale}
                          aria-current={
                            pathname === child.href ? 'page' : undefined
                          }
                          className={cn(
                            'leading-snug',
                            isActive(child.href) && 'bg-accent font-semibold',
                          )}
                        >
                          {child.title}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator className="mx-0 my-2" />
                    <DropdownMenuItem
                      asChild
                      className="cursor-pointer px-3 py-3 font-medium text-primary"
                    >
                      <Link
                        href={item.href}
                        locale={linkLocale}
                        aria-current={
                          pathname === item.href ? 'page' : undefined
                        }
                        className="justify-between gap-3"
                      >
                        {item.allItemsTitle ?? item.title}
                        <ArrowRight
                          aria-hidden="true"
                          className="h-4 w-4 shrink-0"
                        />
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            }

            return (
              <Link
                key={item.href}
                href={item.href}
                locale={linkLocale}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex items-center text-sm font-medium transition-colors',
                  active
                    ? 'font-bold text-foreground'
                    : 'text-muted-foreground hover:text-primary',
                  item.disabled && 'cursor-not-allowed opacity-80',
                )}
              >
                {item.title}
              </Link>
            )
          })}
        </nav>
      ) : null}
    </div>
  )
}
